import { belongsTo, Factory, Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { rand, randNumber, randPastDate } from '@ngneat/falso'
import type { SchemaAttributes } from '@@/api/types'
import {
  filterable,
  pageable,
  PaginationError,
  createErrorResponse,
} from './utils.js'
import { generateFinanceAmount } from '@@/api/mock-server/utils.js'
import { roundEven, toNumber } from '@/utils/number.js'

type ClaimData = SchemaAttributes<'Claim'>

const invoiceClaimStatusMap: Record<number, string> = {
  0: 'Creating',
  1: 'Created',
  2: 'Sending',
  3: 'Sent',
  4: 'Waiting for info',
  5: 'Ready',
  6: 'Rejected',
  7: 'Ready, paid',
  8: 'Documents sent',
}

export const model = Model.extend({
  invoice: belongsTo('invoice'),
}) as ModelDefinition<ClaimData>

export const factory = Factory.extend<ClaimData>({
  claim_type() {
    return rand(['if', 'vetenvoy', 'general'])
  },
  status() {
    return randNumber({
      min: 0,
      max: Object.keys(invoiceClaimStatusMap).length,
    }) as ClaimData['status']
  },
  status_display() {
    return invoiceClaimStatusMap[this.status as number] ?? 'Unknown'
  },
  case_number() {
    return `IC-${randNumber({ min: 1000, max: 9999 })}`
  },
  insurance_company_name() {
    return rand([
      'Agria',
      'If Insurance',
      'Lahitapiola',
      'OP Pohjola',
      'Pohjantahti',
    ])
  },
  insurance_number() {
    return `INS-${randNumber({ min: 100000, max: 999999 })}`
  },
  compensation() {
    return roundEven(toNumber(generateFinanceAmount({ min: 50, max: 2000 })), 2)
  },
  preliminary_sum_for_client() {
    return roundEven(
      toNumber(generateFinanceAmount({ min: 100, max: 3000 })),
      2,
    )
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randPastDate().toISOString()
  },
})

export function routes(server: MockServer) {
  // GET /claims - List insurance claims
  server.get('/claims', function (this: MirageServer, schema, request) {
    const claims = schema.all('insuranceClaim')

    try {
      const filtered = filterable(claims, request, {
        invoice_id: (claim, value) => {
          return String(claim.invoiceId) === String(value)
        },
        claim_type: (claim, value) => {
          return String(claim.claim_type) === String(value)
        },
      })

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    } catch (e) {
      if (e instanceof PaginationError) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: e.message }],
        })
      }
      return createErrorResponse({
        status: 500,
        errors: [{ detail: 'Internal Server Error' }],
      })
    }
  })

  // GET /claims/:id - Retrieve specific insurance claim
  server.get('/claims/:id', function (this: MirageServer, schema, request) {
    const { id } = request.params
    const claim = schema.find('insuranceClaim', id)

    if (!claim) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Insurance claim with id ${id} not found` }],
      })
    }

    return claim
  })
}
